import { useEffect, useRef } from 'react';

type UseInfiniteScrollSentinelProps = {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
};

// plain IntersectionObserver on a trailing sentinel div — no library needed.
function useInfiniteScrollSentinel({
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: UseInfiniteScrollSentinelProps) {
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  // latest values read from a ref so a fetch settling (isFetchingNextPage
  // flipping back to false) doesn't recreate the observer and re-fire onLoadMore
  // for a sentinel that's still intersecting from before.
  const latestRef = useRef({ isFetchingNextPage, onLoadMore });
  latestRef.current = { isFetchingNextPage, onLoadMore };

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasNextPage) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !latestRef.current.isFetchingNextPage) {
          latestRef.current.onLoadMore();
        }
      },
      { threshold: 0 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage]);

  return sentinelRef;
}

export default useInfiniteScrollSentinel;
